import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'i18n'
import { FormProvider, useForm } from 'react-hook-form'
import FButton from '../../../../../components/BaseComponents/FButton'
import FHeading1 from '../../../../../components/BaseComponents/Text/FHeading1'
import FParagraph from '../../../../../components/BaseComponents/Text/FParagraph'
import { Box } from 'rebass'
import FInputRadioGroup from '../../../../../components/BaseComponents/FInputRadioGroup'
import { StepWizardChildProps } from 'react-step-wizard'
import { useBenefitsAnswerQueryQuery, useBenefitsAnswersMutation } from '../../../../../types/graphql'
import FNeedHelpCTA from 'components/BaseComponents/FNeedHelpCTA'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { ga4_useTitle } from 'utils/ga4'
import { LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useSTheme } from 'styles/theme'

interface Props {
    onFinish: () => void
    onPrevious: () => void
    personId: any
    onApplyHeadStart: (value: boolean) => void
}

const HeadStartIntroStep: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()
    const form = useForm({ shouldFocusError: false })
    const { handleSubmit } = form

    const [saveBenefitAnswer, { loading }] = useBenefitsAnswersMutation()
    const { data, loading: queryLoading } = useBenefitsAnswerQueryQuery({
        variables: { personId: props.personId },
        fetchPolicy: 'network-only',
    })

    ga4_useTitle('HeadStart')

    const onFormSubmit = async (values) => {
        await saveBenefitAnswer({
            variables: {
                answers: [
                    {
                        personId: props.personId,
                        category: 'HEAD_START_RE_QUALIFYING',
                        answer: values.head_start_questions,
                    },
                ],
            },
        })
        props.onApplyHeadStart(
            values.head_start_questions === 'head_start_enrolling_first_time' ||
                values.head_start_questions === 'head_start_enrolling_from_transition'
        )
        props.onFinish()
    }
    useEffect(() => {
        if (data) {
            const savedValue = data.benefitsAnswers.find(
                (answer: any) => answer.category === 'HEAD_START_RE_QUALIFYING'
            )?.answer
            form.setValue('head_start_questions', savedValue)
        }
    }, [data])

    const radioDataArray = useMemo(() => {
        return [
            {
                text: t('head_start_enrolling_first_time'),
                id: 'head_start_enrolling_first_time',
            },
            {
                text: t('head_start_enrolling_from_transition'),
                id: 'head_start_enrolling_from_transition',
            },
            {
                text: t('long_beach_none_of_the_above'),
                id: 'long_beach_none_of_the_above',
            },
        ]
    }, [t])

    return (
        <FormProvider {...form}>
            <Box as="form" onSubmit={handleSubmit(onFormSubmit)}>
                <FButtonPrevious onClick={props.onPrevious} />
                <LBTextHeader
                    sx={{
                        textAlign: 'left',
                        fontSize: '32px',
                        color: theme.colors.secondaryColor,
                        lineHeight: '120%',
                        fontFamily: 'Raleway',
                        fontWeight: '900',
                    }}
                >
                    {t('head_start')}
                </LBTextHeader>
                <LBTextSubHeader
                    color={theme.colors.blackLabelfont}
                    fontFamily={theme.fonts.longBeach.body}
                    dangerouslySetInnerHTML={{
                        __html: t('head_start_description_1'),
                    }}
                />

                <FInputRadioGroup
                    radioDataArray={radioDataArray}
                    name={'head_start_questions'}
                    id={'head_start_questions'}
                    minimumSelection={1}
                />

                <FButton
                    buttonType={'filled'}
                    type={'submit'}
                    loading={loading}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('save_and_continue')}
                </FButton>
            </Box>
        </FormProvider>
    )
}

export default HeadStartIntroStep
